import type { GoalStatus, GoalTargetType } from '@pms/shared';
import { cn } from '@/lib/utils';
import {
  GOAL_PROGRESS_TONE_CLASS,
  formatGoalProgressCaption,
  goalProgressTone,
} from './goalConstants';

/** The subset of a goal needed to draw its progress. */
export interface GoalProgressShape {
  target_type: GoalTargetType;
  current_value: number;
  target_value: number | null;
  unit: string | null;
  milestone_done: number;
  milestone_total: number;
  progress_percent: number;
  status: GoalStatus;
  is_overdue: boolean;
  is_at_risk: boolean;
}

interface GoalProgressBarProps {
  goal: GoalProgressShape;
  /** `sm` for table rows, `md` for cards and the detail drawer. */
  size?: 'sm' | 'md';
  /** Hide the "820K / 1M" line when the surface is already tight. */
  showCaption?: boolean;
  className?: string;
}

/**
 * The single progress bar for the Goals module. `progress_percent` is computed
 * server-side (clamped 0-100) so every surface shows the same number; the caption
 * still shows the raw values, which is how over-achievement stays visible.
 */
export function GoalProgressBar({
  goal,
  size = 'md',
  showCaption = true,
  className,
}: GoalProgressBarProps) {
  const tone = goalProgressTone(goal);
  const percent = Math.min(100, Math.max(0, goal.progress_percent));

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'w-full overflow-hidden rounded-full bg-muted',
          size === 'sm' ? 'h-1.5' : 'h-2',
        )}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Goal progress"
      >
        <div
          className={cn('h-full rounded-full transition-all duration-500', GOAL_PROGRESS_TONE_CLASS[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>
      {showCaption && (
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span className="truncate">{formatGoalProgressCaption(goal)}</span>
          <span className="shrink-0 font-semibold tabular-nums text-foreground">{percent}%</span>
        </div>
      )}
    </div>
  );
}

interface GoalProgressRingProps {
  /** 0-100. */
  percent: number;
  /** Pixel diameter. */
  size?: number;
  label?: string;
  sub?: string;
  className?: string;
}

/**
 * Aggregate completion dial for the My Goals header — the same SVG treatment the
 * Staff Performance tab uses for its score ring.
 */
export function GoalProgressRing({
  percent,
  size = 120,
  label,
  sub,
  className,
}: GoalProgressRingProps) {
  const STROKE = 10;
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, percent));

  return (
    <div className={cn('relative shrink-0', className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={STROKE}
          className="stroke-white/25"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          className="stroke-white transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-2xl font-semibold leading-none tracking-tight text-white">
          {Math.round(clamped)}%
        </span>
        {label && <span className="mt-1 text-[11px] font-medium text-white/80">{label}</span>}
        {sub && <span className="text-[10px] text-white/60">{sub}</span>}
      </div>
    </div>
  );
}
